import { createContext, useContext, useMemo } from 'react'

import { useDraggable, useDroppable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'

import { useDraggingCue } from './cue-dnd.store'
import { CueDropzone, DroppableId, isDropDisabled } from './cue.dnd'

type UseCueDropzoneParams = {
  dropzone: CueDropzone
  /** The cue owning this zone. */
  cueId: string
  parentId?: string | null
}

/**
 * Registers one dropzone for a cue.
 *
 * Covers all four zones, since they differ only in their id and in whether the
 * current drag is allowed to land (see `isDropDisabled`).
 *
 * Takes primitives rather than a `DropTarget` object so callers do not build a
 * fresh object on every render.
 */
export function useCueDropzone({
  dropzone,
  cueId,
  parentId,
}: UseCueDropzoneParams) {
  const draggingCue = useDraggingCue()

  return useDroppable({
    id: DroppableId[dropzone](cueId),
    disabled: isDropDisabled(dropzone, draggingCue, { id: cueId, parentId }),
  })
}

/**
 * Makes a cue row draggable.
 */
export function useCueDraggable(cueId: string) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform } =
    useDraggable({ id: cueId })

  // Both are stable across a drag, so the handle does not re-render with the row.
  const dragHandle = useMemo<CueDragHandleValue>(
    () => ({ listeners, setActivatorNodeRef }),
    [listeners, setActivatorNodeRef],
  )

  return {
    attributes,
    setNodeRef,
    style: { transform: CSS.Transform.toString(transform) },
    dragHandle,
  }
}

type CueDragHandleValue = Pick<
  ReturnType<typeof useDraggable>,
  'listeners' | 'setActivatorNodeRef'
>

/**
 * The row's drag handle bindings, provided by `CueDraggable`.
 *
 * A context rather than a second `useDraggable` for the same id: dnd-kit keeps
 * one registration per id, and a second hook mounting later would replace the
 * row's with one that has no node to measure. The default is inert, for rows
 * rendered outside the list such as the drag overlay.
 */
export const CueDragHandleContext = createContext<CueDragHandleValue>({
  listeners: undefined,
  setActivatorNodeRef: () => {},
})

/**
 * The grab handle for a cue row.
 *
 * The handle lives in the cue controls column while the draggable node is the
 * row wrapper, so it reads the wrapper's bindings from context.
 */
export function useCueDragHandle() {
  return useContext(CueDragHandleContext)
}
